/**
 * `/admin/:insee/notes`: Revue des notes (`docs/ADMIN.md` §2 and §4) — what
 * must be deleted this week. Flagged notes first, each with its computed
 * reason and the two actions the mockup draws: *Purger* (destructive,
 * confirmed) and *Garder*.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getCommuneNotes, keepNote, purgeNote } from '../../../api/admin';
import { ApiError } from '../../../api/client';
import { Badge, Button, Select, Sheet, Skeleton, useToast } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { formatDate } from '../format';

import type { ApiErrorBody } from '../../../api/types';
import type { AdminNote } from '../types';
import type { NotesFlaggedFilter } from '../../../api/admin';

const FILTER_OPTIONS: Array<{ value: NotesFlaggedFilter; label: string }> = [
  { value: 'all', label: t('admin.notes.filter.all') },
  { value: 'flagged', label: t('admin.notes.filter.flagged') },
  { value: 'unflagged', label: t('admin.notes.filter.unflagged') },
];

/**
 * Render the Revue des notes screen.
 *
 * @returns The page element.
 */
export function NotesPage() {
  const { insee = '' } = useParams<{ insee: string }>();
  const { showToast } = useToast();
  const [notes, setNotes] = useState<AdminNote[] | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState<NotesFlaggedFilter>('all');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<AdminNote | null>(null);

  useEffect(() => {
    let cancelled = false;
    setNotes(undefined);
    setLoadError(false);
    getCommuneNotes(insee, filter)
      .then((result) => {
        if (!cancelled) setNotes(result.results);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [insee, filter]);

  function replaceNote(updated: AdminNote) {
    setNotes((current) => current?.map((note) => (note.id === updated.id ? updated : note)));
  }

  async function handleKeep(note: AdminNote) {
    setBusyId(note.id);
    try {
      await keepNote(note.id);
      replaceNote({ ...note, note_flagged: false });
      showToast(t('admin.notes.keepSuccess'), 'success');
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setBusyId(null);
    }
  }

  async function handlePurge(note: AdminNote) {
    setBusyId(note.id);
    try {
      await purgeNote(note.id);
      replaceNote({ ...note, note: '', note_flagged: false, note_categories: [], note_reason: '' });
      showToast(t('admin.notes.purgeSuccess'), 'success');
      setConfirmTarget(null);
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <h1>{t('admin.notes.title')}</h1>
        <p className="admin-lead">{t('admin.notes.lead')}</p>
      </div>

      <Select
        label={t('admin.notes.filterLabel')}
        value={filter}
        onChange={(event) => setFilter(event.target.value as NotesFlaggedFilter)}
        options={FILTER_OPTIONS}
      />

      {loadError ? (
        <p role="alert" className="admin-error">
          {t('admin.notes.loadError')}
        </p>
      ) : notes === undefined ? (
        <>
          <Skeleton height="6rem" />
          <Skeleton height="6rem" />
        </>
      ) : notes.length === 0 ? (
        <p className="admin-muted">{t('admin.notes.empty')}</p>
      ) : (
        <ul className="admin-notes">
          {notes.map((note) => (
            <li
              key={note.id}
              className={note.note_flagged ? 'admin-note admin-note--flagged' : 'admin-note'}
            >
              <div className="admin-note__head">
                <strong>{note.door_label}</strong>
                {note.note_flagged ? (
                  <Badge tone="red">{t('admin.notes.reason', { reason: note.note_reason })}</Badge>
                ) : null}
              </div>
              <p className="admin-note__text">{note.note}</p>
              <p className="admin-muted">
                {t('admin.notes.recordedAt', {
                  date: formatDate(note.recorded_at),
                  author: note.user.first_name,
                })}
              </p>
              <div className="admin-note__actions">
                <Button
                  variant="danger"
                  disabled={busyId === note.id}
                  onClick={() => setConfirmTarget(note)}
                >
                  {t('admin.notes.purge')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={busyId === note.id}
                  onClick={() => void handleKeep(note)}
                >
                  {t('admin.notes.keep')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Sheet
        open={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        title={t('admin.notes.purgeConfirmTitle')}
      >
        <p>{t('admin.notes.purgeConfirmBody')}</p>
        <Button
          variant="danger"
          fullWidth
          disabled={confirmTarget !== null && busyId === confirmTarget.id}
          onClick={() => confirmTarget && void handlePurge(confirmTarget)}
        >
          {t('admin.notes.purgeConfirmAction')}
        </Button>
      </Sheet>
    </div>
  );
}

/** Resolve the French message for a caught notes-action API error. */
function errorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    const body = cause.body as ApiErrorBody | undefined;
    if (body?.detail) return body.detail;
  }
  return t('admin.notes.actionError');
}
